/**
 * KillListEntriesPage — System · Kill List.
 *
 * The blocklist of CIFs, accounts, cards, and online-banking enrollments
 * queued for decommissioning. The runtime auto-populates this from cart
 * archival and Test Mode; this screen lets an admin add or edit individual
 * rows. Compliance-sensitive — every row names a real target ID that, once
 * executed, removes a customer artifact from PCB systems.
 *
 * Contract (verified against the backend 2026-06-16 — entity, read route,
 * upsert DTO, and service all share identical camelCase field names; NO drift):
 *   - Entity:  PCBJxChangeApi/Data/Entities/KillListEntry.cs
 *              (KillListEntryId/EntryType/TargetId/TargetType/Reason/
 *              Status/RetentionHold/CreatedBy/CreatedAt/… — NO IsActive column;
 *              "cancellation" is a Status value, not a soft-delete flag.)
 *   - Read:    GET /v3/config/kill-list-entries
 *              → V3ConfigFunction.KillListEntriesList. Returns the LATEST 2000
 *              rows ordered by CreatedAt DESC with NO status/active filter — so
 *              cancelled rows stay visible (audit), and a soft-cancel never
 *              vanishes a row irrecoverably. There is no typed config method for
 *              this route, so the read uses a raw client.http.get with the exact
 *              inline wire type (mirrors NaicsCodesPage's raw-read pattern).
 *   - Write:   POST /v3/admin/kill-list-entries
 *              → AdminSystemService.UpsertKillListEntryAsync (EF createOrUpdate,
 *              natural key KillListEntryId GUID). The service persists EVERY
 *              supplied field on both the create and update branches
 *              (entryType/targetId/targetType/reason/status/retentionHold) —
 *              no silent drop.
 *   - Core:    POST /v3/admin/kill-list-entries/core-status (BACKLOG-1757,
 *              Bill 2026-10-06) — "Refresh from Core Director": reads Core
 *              Director in batches and stamps each entry's coreKillStatus, which
 *              the read route returns. Changes no entry's status.
 *   - Delete:  DELETE /v3/admin/kill-list-entries/{killListEntryId}
 *              → AdminSystemService.CancelKillListEntryAsync. SOFT-CANCEL: sets
 *              Status='cancelled', the row is NOT removed. The entity has no
 *              IsActive column and the read route does not filter it out, so the
 *              cancelled row remains visible and can be re-activated by editing
 *              Status back. This is the safe choice (no irrecoverable loss).
 *
 * Migrated 2026-06-16 from ROP-teams Admin
 * (apps/ROP-teams/src/modules/Admin/editors/killListEntriesEditor.tsx — the
 * operational app writes no config). Field names + write/read routes are the
 * verbatim verified wire contract.
 * Auth: ADMIN_AUTH_TOKEN_SENTINEL (MSAL bearer; no secret in the bundle).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ADMIN_AUTH_TOKEN_SENTINEL,
  ApiError,
  type KillListEntryUpsertBody,
} from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, BoolField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

/** The entry's last Core Director read (BACKLOG-1757); null until first refreshed. */
interface CoreKillStatus {
  at: string;
  existence: 'exists' | 'gone' | 'inconclusive';
  marker: 'marked' | 'notmarked' | 'notapplicable' | 'unreadable';
  markerValue: string | null;
  detail: string;
}

interface KillRow {
  killListEntryId: string;
  entryType: string;
  targetId: string;
  targetType: string | null;
  customerName: string | null;
  coreKillStatus: CoreKillStatus | null;
  reason: string;
  status: string;
  createdBy: string | null;
  createdAt: string;
  verifiedBy: string | null;
  verifiedAt: string | null;
  executedAt: string | null;
  failedReason: string | null;
  retentionHold: boolean;
}

interface EditDraft {
  isNew: boolean;
  killListEntryId: string | null;
  entryType: string;
  targetId: string;
  targetType: string | null;
  customerName: string | null;
  reason: string;
  status: string;
  retentionHold: boolean;
}

const ENTRY_TYPES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'CifKill', label: 'CIF Kill — decommission a customer information file' },
  { value: 'AccountKill', label: 'Account Kill — close an individual deposit / loan account' },
  { value: 'CardRevoke', label: 'Card Revoke — revoke a debit / ATM card' },
  { value: 'NetTellerRevoke', label: 'NetTeller Revoke — disable an online-banking enrollment' },
];

const REASONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'AbandonedCart', label: 'Abandoned cart' },
  { value: 'PartialCommit', label: 'Partial commit (rollback)' },
  { value: 'FraudFlagged', label: 'Fraud flagged' },
  { value: 'AdverseAction', label: 'Adverse action' },
  { value: 'ComplianceHold', label: 'Compliance hold' },
  { value: 'DuplicateCif', label: 'Duplicate CIF' },
  { value: 'SsnChangedPostCreate', label: 'SSN changed after create' },
  { value: 'TestMode', label: 'Test Mode (test CIF)' },
  { value: 'Manual', label: 'Manual entry' },
  { value: 'Other', label: 'Other' },
];

const STATUSES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'pending-verification', label: 'Pending verification' },
  { value: 'verified', label: 'Verified' },
  { value: 'executed', label: 'Executed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'err' | 'info' | 'muted'> = {
  'pending-verification': 'info',
  verified: 'warn',
  executed: 'ok',
  failed: 'err',
  cancelled: 'muted',
};

function emptyDraft(): EditDraft {
  return {
    isNew: true,
    killListEntryId: null,
    entryType: 'CifKill',
    targetId: '',
    targetType: null,
    customerName: null,
    reason: 'Manual',
    status: 'pending-verification',
    retentionHold: false,
  };
}

function rowToDraft(r: KillRow): EditDraft {
  return {
    isNew: false,
    killListEntryId: r.killListEntryId,
    entryType: r.entryType,
    targetId: r.targetId,
    targetType: r.targetType,
    customerName: r.customerName,
    reason: r.reason,
    status: r.status,
    retentionHold: r.retentionHold,
  };
}

function draftToUpsertBody(d: EditDraft): KillListEntryUpsertBody {
  return {
    killListEntryId: d.killListEntryId,
    entryType: d.entryType,
    targetId: d.targetId.trim(),
    targetType: d.targetType && d.targetType.trim() ? d.targetType.trim() : null,
    customerName: d.customerName && d.customerName.trim() ? d.customerName.trim() : null,
    reason: d.reason,
    status: d.status,
    retentionHold: d.retentionHold,
  };
}

function fmtTimestamp(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * In Core column (BACKLOG-1757). Gone is the finished state. A record still in Core
 * carrying its marker (F5 = KILL, or card F103 = H / D) is waiting for Core Director's
 * own removal. "Not marked" is only a problem once the entry says executed.
 */
function inCoreCell(r: KillRow): { tone: 'ok' | 'warn' | 'err' | 'info' | 'muted'; text: string } {
  const k = r.coreKillStatus;
  if (!k) return { tone: 'muted', text: 'not checked' };
  if (k.existence === 'gone') return { tone: 'ok', text: 'gone' };
  if (k.existence === 'inconclusive') return { tone: 'muted', text: 'inconclusive' };
  switch (k.marker) {
    case 'marked':
      return { tone: 'info', text: `in Core · ${k.markerValue === 'KILL' || !k.markerValue ? 'KILL' : `card ${k.markerValue}`}` };
    case 'notmarked':
      return { tone: r.status === 'executed' ? 'err' : 'muted', text: 'in Core · not marked' };
    case 'notapplicable':
      return { tone: 'muted', text: 'in Core' };
    default:
      return { tone: 'muted', text: 'in Core · unreadable' };
  }
}

function statusLabel(value: string): string {
  return STATUSES.find((s) => s.value === value)?.label ?? value;
}

export function KillListEntriesPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<KillRow> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<KillRow | null>(null);
  const [toast, setToast] = useState<{ tone: 'info' | 'warn'; text: string } | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [coreProgress, setCoreProgress] = useState<{ checked: number; total: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setRows(null);
      setLoadError(null);
      try {
        // No typed config method exists for this route; read raw with the
        // exact inline wire type (camelCase, all rows incl. cancelled).
        const resp = await client.http.get<{
          success: boolean;
          data?: ReadonlyArray<KillRow>;
          error?: string;
        }>('/v3/config/kill-list-entries');
        if (cancelled) return;
        if (resp.success && Array.isArray(resp.data)) {
          setRows(resp.data);
        } else {
          setLoadError(resp.error ?? 'Failed to load kill-list entries.');
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(
          ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e),
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, refreshNonce]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    return (rows ?? [])
      .slice()
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      .filter((r) => {
        if (statusFilter && r.status !== statusFilter) return false;
        if (!needle) return true;
        const hay = `${r.targetId} ${r.targetType ?? ''} ${r.customerName ?? ''} ${r.entryType} ${r.reason}`.toLowerCase();
        return hay.includes(needle);
      });
  }, [rows, filter, statusFilter]);

  const columns: ReadonlyArray<DataTableColumn<KillRow>> = useMemo(
    () => [
      {
        header: 'Created',
        cell: (r) => <span className="cell-desc">{fmtTimestamp(r.createdAt)}</span>,
        sortKey: (r) => r.createdAt,
      },
      {
        // Bill 2026-07-28: Type is the Core Director record type
        // (CIF/DDA/SAV/CD/IRA/BOX/NT/ATM), not the executor's routing
        // discriminator. Fall back to entryType only for legacy rows that
        // predate the vocabulary.
        header: 'Type',
        cell: (r) => <span className="cell-strong">{r.targetType ?? r.entryType}</span>,
        sortKey: (r) => r.targetType ?? r.entryType,
      },
      {
        header: 'Target',
        cell: (r) => <code className="cell-mono--bold">{r.targetId}</code>,
      },
      {
        // Full name of the primary CIF on the account, or of the CIF itself.
        header: 'Customer',
        cell: (r) =>
          r.customerName ? (
            <span>{r.customerName}</span>
          ) : (
            <span className="cell-desc">&mdash;</span>
          ),
        sortKey: (r) => r.customerName ?? '',
      },
      { header: 'Reason', cell: (r) => <span className="cell-desc">{r.reason}</span> },
      {
        header: 'Status',
        cell: (r) => (
          <Pill tone={STATUS_TONE[r.status] ?? 'muted'}>{statusLabel(r.status)}</Pill>
        ),
      },
      {
        header: 'In Core',
        cell: (r) => {
          const c = inCoreCell(r);
          const k = r.coreKillStatus;
          return (
            <span title={k ? `${k.detail}
Checked ${fmtTimestamp(k.at)}` : 'Not yet checked against Core Director'}>
              <Pill tone={c.tone}>{c.text}</Pill>
            </span>
          );
        },
        sortKey: (r) => inCoreCell(r).text,
      },
      {
        header: 'Hold',
        cell: (r) =>
          r.retentionHold ? <Pill tone="warn">hold</Pill> : <Pill tone="muted">no</Pill>,
      },
      {
        header: '',
        cell: (r) => (
          <div className="card-actions-row">
            <Button onClick={() => { setDraft(rowToDraft(r)); setSaveError(null); }}>Edit</Button>
            {r.status !== 'cancelled' && (
              <Button onClick={() => setCancelTarget(r)}>Cancel</Button>
            )}
          </div>
        ),
      },
    ],
    [],
  );

  const handleSave = useCallback(async () => {
    if (!draft) return;
    if (!draft.targetId.trim()) {
      setSaveError('Target ID is required.');
      return;
    }
    if (!draft.entryType.trim()) {
      setSaveError('Entry type is required.');
      return;
    }
    if (!draft.reason.trim()) {
      setSaveError('Reason is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const resp = await client.retail.admin.killListEntries.upsert(
        ADMIN_AUTH_TOKEN_SENTINEL,
        draftToUpsertBody(draft),
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'admin upsert returned success=false');
        return;
      }
      setDraft(null);
      setToast({
        tone: 'info',
        text: draft.isNew
          ? `Added ${draft.entryType} for ${draft.targetId.trim()}.`
          : `Updated ${draft.entryType} ${draft.targetId.trim()}.`,
      });
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [draft, client, refresh]);

  // BACKLOG-1757: run every batch of one refresh, showing progress, then reload the list.
  const handleCoreRefresh = useCallback(async () => {
    const total = (rows ?? []).filter((r) => r.status !== 'cancelled').length;
    setCoreProgress({ checked: 0, total });
    setToast(null);
    const sum = { checked: 0, gone: 0, marked: 0, notMarked: 0, inconclusive: 0 };
    let runStartedAt: string | undefined;
    try {
      for (;;) {
        const resp = await client.retail.admin.killListEntries.coreStatus(
          ADMIN_AUTH_TOKEN_SENTINEL,
          runStartedAt ? { runStartedAt } : {},
        );
        if (!resp.success || !resp.data) {
          throw new Error(resp.error ?? 'Core Director refresh returned success=false');
        }
        const d = resp.data;
        runStartedAt = d.runStartedAt;
        sum.checked += d.checked;
        sum.gone += d.gone;
        sum.marked += d.marked;
        sum.notMarked += d.notMarked;
        sum.inconclusive += d.inconclusive;
        setCoreProgress({ checked: sum.checked, total: sum.checked + d.remaining });
        if (d.remaining === 0 || d.checked === 0) break;
      }
      setToast({
        tone: sum.notMarked > 0 || sum.inconclusive > 0 ? 'warn' : 'info',
        text:
          `Checked ${sum.checked} entries against Core Director: ${sum.gone} gone, ` +
          `${sum.marked} still in Core with the kill marker, ${sum.notMarked} in Core without it, ` +
          `${sum.inconclusive} inconclusive. Hover a row's In Core value for the detail.`,
      });
    } catch (e) {
      setToast({
        tone: 'warn',
        text:
          `Core Director refresh stopped after ${sum.checked} entries: ` +
          (ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e)) +
          '. Entries already checked keep their result; refresh again to continue.',
      });
    } finally {
      setCoreProgress(null);
      refresh();
    }
  }, [rows, client, refresh]);

  const handleCancel = useCallback(async () => {
    if (!cancelTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Soft-cancel: sets Status='cancelled'. The row stays for audit and the
      // executor skips it on the next pass. Flip Status back via Edit to restore.
      const resp = await client.retail.admin.killListEntries.cancel(
        ADMIN_AUTH_TOKEN_SENTINEL,
        cancelTarget.killListEntryId,
      );
      if (!resp.success) {
        setSaveError(resp.error ?? 'cancel returned success=false');
        return;
      }
      setToast({
        tone: 'warn',
        text: `${cancelTarget.entryType} for ${cancelTarget.targetId} is now cancelled. The executor will skip it.`,
      });
      setCancelTarget(null);
      refresh();
    } catch (e) {
      setSaveError(ApiError.isApiError(e) ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [cancelTarget, client, refresh]);

  if (loadError) {
    return (
      <Banner tone="err">
        Could not load kill-list entries: {loadError} <Button onClick={refresh}>Retry</Button>
      </Banner>
    );
  }
  if (!rows) return <Spinner label="Loading kill-list entries…" />;

  return (
    <>
      {toast && (
        <Banner tone={toast.tone}>
          {toast.text} <Button onClick={() => setToast(null)}>Dismiss</Button>
        </Banner>
      )}

      <Banner tone="warn">
        Treat this list with gravity. Every row names a real CIF, account, card, or NetTeller
        enrollment queued for decommissioning. The runtime auto-populates it from cart archival and
        Test Mode; manual edits here queue a real action against PCB systems, so confirm the target
        ID before saving. Cancelling a row sets its status to <code className="cell-mono">cancelled</code>{' '}
        (kept for audit, never deleted).
      </Banner>

      <Card
        title={`Kill List — ${visible.length} of ${rows.length}`}
        actions={
          <div className="card-actions-row">
            <div className="users-filter">
              <TextField
                label=""
                value={filter}
                onChange={setFilter}
                placeholder="target ID, type, or reason…"
              />
            </div>
            <Button onClick={() => void handleCoreRefresh()} disabled={coreProgress !== null}>
              {coreProgress
                ? `Checking Core Director… ${coreProgress.checked} of ${coreProgress.total}`
                : 'Refresh from Core Director'}
            </Button>
            <Button variant="primary" onClick={() => { setDraft(emptyDraft()); setSaveError(null); }}>
              + Add Entry
            </Button>
          </div>
        }
      >
        <div className="users-filter">
          <SelectField
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: '', label: 'All statuses' }, ...STATUSES]}
          />
        </div>
        <p className="cell-desc">
          The most recent 2000 entries, newest first. Cancelled rows stay listed for audit — filter
          by status to focus on live work.
        </p>
        <DataTable<KillRow>
          columns={columns}
          rows={visible}
          rowKey={(r) => r.killListEntryId}
          emptyState={<p className="empty">No kill-list entries match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={draft?.isNew ? 'Add a kill-list entry' : `Edit ${draft?.targetId ?? ''}`}
        onClose={() => {
          if (saving) return;
          setDraft(null);
          setSaveError(null);
        }}
        width={620}
        actions={
          draft && (
            <>
              <Button onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
                {saving ? 'Saving…' : draft.isNew ? 'Create' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {draft && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}

            <Banner tone="warn">
              Adding a row here queues a real action against PCB systems. Make sure you have the
              right target ID before saving.
            </Banner>

            <div className="form-row">
              <SelectField
                label="Entry type"
                required
                value={draft.entryType}
                onChange={(v) => setDraft({ ...draft, entryType: v })}
                options={ENTRY_TYPES}
                hint="What kind of artifact is being decommissioned."
              />
              <SelectField
                label="Status"
                required
                value={draft.status}
                onChange={(v) => setDraft({ ...draft, status: v })}
                options={STATUSES}
                hint="Set to Cancelled to take a row out of the executor; flip back to restore it."
              />
            </div>

            <div className="form-row">
              <TextField
                label="Target ID"
                required
                value={draft.targetId}
                onChange={(v) => setDraft({ ...draft, targetId: v.slice(0, 64) })}
                monospace
                placeholder="CIF, account, card last-4, or NetTeller ID"
                hint="The CIF, account number, card last-4, or NetTeller ID being decommissioned."
              />
              <TextField
                label="Type"
                value={draft.targetType ?? ''}
                onChange={(v) =>
                  setDraft({ ...draft, targetType: v.slice(0, 64).toUpperCase() || null })
                }
                placeholder="DDA"
                hint="Core Director record type — CIF, DDA, SAV, CD, IRA, BOX, NT, ATM."
              />
            </div>

            <TextField
              label="Customer"
              value={draft.customerName ?? ''}
              onChange={(v) => setDraft({ ...draft, customerName: v.slice(0, 120) || null })}
              placeholder="Full name"
              hint="Full name of the primary CIF on the account, or of the CIF itself."
            />

            <SelectField
              label="Reason"
              required
              value={draft.reason}
              onChange={(v) => setDraft({ ...draft, reason: v })}
              options={REASONS}
              hint="Why this artifact is queued for removal. Drives downstream audit reporting."
            />

            <BoolField
              label="Retention hold"
              value={draft.retentionHold}
              onChange={(v) => setDraft({ ...draft, retentionHold: v })}
              hint="Pause auto-purge for legal-hold cases. The row stays until the hold is released."
            />

            {!draft.isNew && draft.killListEntryId && (
              <TextField
                label="Entry ID"
                value={draft.killListEntryId}
                onChange={() => undefined}
                disabled
                monospace
                hint="Immutable GUID natural key. Read-only."
              />
            )}
          </div>
        )}
      </ModalShell>

      <ModalShell
        open={cancelTarget !== null}
        title="Cancel this entry?"
        onClose={() => {
          if (saving) return;
          setCancelTarget(null);
          setSaveError(null);
        }}
        width={520}
        actions={
          cancelTarget && (
            <>
              <Button onClick={() => setCancelTarget(null)} disabled={saving}>
                Keep it
              </Button>
              <Button variant="primary" onClick={() => void handleCancel()} disabled={saving}>
                {saving ? 'Working…' : 'Cancel entry'}
              </Button>
            </>
          )
        }
      >
        {cancelTarget && (
          <div className="form-stack">
            {saveError && <Banner tone="err">{saveError}</Banner>}
            <p className="modal-prose">
              We&apos;ll set this <strong>{cancelTarget.entryType}</strong> entry for{' '}
              <code className="cell-mono">{cancelTarget.targetId}</code> to status{' '}
              <strong>cancelled</strong>. The executor will skip it on the next pass. The row stays
              listed for audit, and you can flip its status back anytime via Edit.
            </p>
          </div>
        )}
      </ModalShell>
    </>
  );
}
